import { z } from 'zod';

/**
 * Messaging & Inbox (Phase 3). Schema names are deliberately distinct from the
 * AI Co-Pilot's `createConversationRequestSchema` / `sendMessageRequestSchema`
 * so the two messaging surfaces never collide.
 */

export const conversationTypeSchema = z.enum(['DIRECT', 'GROUP']);
export type ConversationType = z.infer<typeof conversationTypeSchema>;

const messageBody = z
  .string()
  .trim()
  .min(1, 'Type a message.')
  .max(4000, 'Message must be 4000 characters or fewer');

/** Start (or reuse) a 1:1 conversation with one other person. */
export const createDmRequestSchema = z.object({
  userId: z.string().uuid('Pick someone to message.'),
});
export type CreateDmRequest = z.infer<typeof createDmRequestSchema>;

/** Create a group conversation with a title and an initial participant set. */
export const createGroupChatRequestSchema = z.object({
  title: z.string().trim().min(1, 'Give the group a name.').max(120, 'Name is too long'),
  participantIds: z
    .array(z.string().uuid())
    .min(1, 'Add at least one other person.')
    .max(50, 'Too many participants'),
});
export type CreateGroupChatRequest = z.infer<typeof createGroupChatRequestSchema>;

export const addParticipantsRequestSchema = z.object({
  userIds: z.array(z.string().uuid()).min(1, 'Add at least one person.').max(50),
});
export type AddParticipantsRequest = z.infer<typeof addParticipantsRequestSchema>;

export const postMessageRequestSchema = z.object({
  body: messageBody,
  mentionedUserIds: z.array(z.string().uuid()).max(50).optional(),
});
export type PostMessageRequest = z.infer<typeof postMessageRequestSchema>;

export const editMessageRequestSchema = z.object({
  body: messageBody,
});
export type EditMessageRequest = z.infer<typeof editMessageRequestSchema>;

/** Incremental poll cursor — fetch messages created after this id. */
export const listMessagesQuerySchema = z.object({
  after: z.string().uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ListMessagesQuery = z.infer<typeof listMessagesQuerySchema>;

export const messageSearchQuerySchema = z.object({
  q: z.string().trim().min(1, 'Enter something to search for.').max(200),
});
export type MessageSearchQuery = z.infer<typeof messageSearchQuerySchema>;

// -------------------- DTOs (ISO date strings) --------------------

export interface ConversationParticipantDto {
  userId: string;
  name: string;
  rank: string | null;
  avatarColor: string | null;
  /** When this participant last read the conversation — drives "Seen" receipts. */
  lastReadAt: string | null;
}

export interface ConversationListItemDto {
  id: string;
  type: ConversationType;
  /** Group title; null for DMs (the UI derives the name from `otherParticipants`). */
  title: string | null;
  /** Everyone except the viewer — drives the DM name + group avatars. */
  otherParticipants: ConversationParticipantDto[];
  lastMessagePreview: string | null;
  lastMessageAt: string | null;
  unreadCount: number;
}

export interface ConversationDetailDto {
  id: string;
  type: ConversationType;
  title: string | null;
  createdById: string;
  participants: ConversationParticipantDto[];
}

export interface MessageDto {
  id: string;
  conversationId: string;
  senderId: string;
  senderName: string;
  senderAvatarColor: string | null;
  /** Null when the message has been deleted (soft-delete). */
  body: string | null;
  editedAt: string | null;
  deletedAt: string | null;
  mentionedUserIds: string[];
  createdAt: string;
}

export interface InboxUnreadResponse {
  total: number;
}
